import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Linkedin, MapPin, Phone, Mail, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Brands", path: "/brands" },
  { name: "Contact", path: "/contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "nav-glass-scrolled" : "nav-glass"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 bg-[#0A192F] rounded-sm flex items-center justify-center">
            <span className="text-white font-bold text-lg font-['Outfit']">A</span>
          </div>
          <div className="hidden sm:block">
            <span className="font-['Outfit'] font-bold text-[#0A192F] text-base leading-tight block">
              Assistive Netspeed
            </span>
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#64748B]">
              Technologies Pvt Ltd
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-medium transition-colors duration-200 hover:text-[#EA580C] ${
                location.pathname === link.path
                  ? "text-[#EA580C]"
                  : "text-[#0A192F]"
              }`}
            >
              {link.name}
            </Link>
          ))}
          <Link to="/contact">
            <Button
              data-testid="nav-get-quote-btn"
              className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm px-6 text-sm"
            >
              Get a Quote
            </Button>
          </Link>
        </nav>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6 text-[#0A192F]" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80 bg-white">
            <div className="flex flex-col gap-6 mt-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-lg font-medium font-['Outfit'] transition-colors hover:text-[#EA580C] ${
                    location.pathname === link.path
                      ? "text-[#EA580C]"
                      : "text-[#0A192F]"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/contact" onClick={() => setOpen(false)}>
                <Button
                  data-testid="mobile-get-quote-btn"
                  className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm w-full"
                >
                  Get a Quote
                </Button>
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}

export function Footer() {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <footer data-testid="main-footer" className="bg-[#0A192F] text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-[#EA580C] rounded-sm flex items-center justify-center">
                <span className="text-white font-bold text-lg font-['Outfit']">A</span>
              </div>
              <span className="font-['Outfit'] font-bold text-white text-base">
                Assistive Netspeed
              </span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-5">
              Empowering India's digital infrastructure with cutting-edge ISP, IPTV, and telecom solutions since inception.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.linkedin.com/company/13628123/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="w-9 h-9 border border-slate-600 rounded-sm flex items-center justify-center hover:border-[#EA580C] hover:bg-[#EA580C] transition-all duration-200"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Outfit'] font-semibold text-sm uppercase tracking-[0.15em] text-[#EA580C] mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-slate-400 hover:text-[#EA580C] transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-['Outfit'] font-semibold text-sm uppercase tracking-[0.15em] text-[#EA580C] mb-5">
              Our Services
            </h4>
            <ul className="space-y-3">
              {["Internet Service Provider", "IPTV Solutions", "System Integration", "Web Development", "MPLS Connectivity"].map((s) => (
                <li key={s}>
                  <Link
                    to="/services"
                    className="text-sm text-slate-400 hover:text-[#EA580C] transition-colors duration-200"
                  >
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Outfit'] font-semibold text-sm uppercase tracking-[0.15em] text-[#EA580C] mb-5">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 text-[#EA580C] mt-1 flex-shrink-0" />
                <span className="text-sm text-slate-400">
                  #330, Whitecity Layout, Opp to Blossom International School, Bengaluru-560049
                </span>
              </li>
              <li className="flex gap-3">
                <Phone className="w-4 h-4 text-[#EA580C] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-400">+91 99864 56688</span>
              </li>
              <li className="flex gap-3">
                <Mail className="w-4 h-4 text-[#EA580C] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-400">info@assistivenetwork.com</span>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="my-10 bg-slate-700" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} Assistive Netspeed Technologies Pvt Ltd. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-xs text-slate-500 hover:text-[#EA580C] transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-xs text-slate-500 hover:text-[#EA580C] transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>

      {/* Scroll to top */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="scroll-to-top-btn"
          className="fixed bottom-6 right-6 w-11 h-11 bg-[#EA580C] text-white rounded-sm shadow-lg flex items-center justify-center hover:bg-[#C2410C] transition-colors z-50 animate-fade-in"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      )}
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-20">{children}</main>
      <Footer />
    </>
  );
}
